import { useCallback, useRef, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

// local imports
import { ProfileService, SystemUserService } from 'app/services';
import { cancelRequests } from 'app/utils/axios';
import { useStateCallback } from 'app/hooks';
import { useGetSystemUserRolesService, useSystemUserDetailService } from 'app/hooks/services';
import { updateUserProfile } from 'app/auth/store/userSlice';
import { EnvDefaults, FormatUtils, ValidationSchema } from 'app/utils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import SystemUserCard from './system-user-card';
import { ChangePasswordForm, SystemUserForm } from 'app/components/organisms/forms';
import {
  Breadcrumb,
  ConfirmDialog,
  CustomCard,
  Loader,
  NoDataTableComponent
} from 'app/components/atoms';

const SystemUserEdit = ({ match }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const passwordFormRef = useRef();
  const profileId = useSelector(({ auth }) => auth?.user?.data.id);
  const userId = match?.params?.id;
  const isItMe = userId === profileId;
  const isEdit = !!userId;

  // states
  const [loading, setLoading] = useStateCallback(false);
  const [passwordLoading, setPasswordLoading] = useStateCallback(false);
  const [deleteLoading, setDeleteLoading] = useStateCallback(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [confirmAlert, setConfirmAlert] = useState(false);

  // Fetch user detail data first;
  const { service, handleRefresh } = useSystemUserDetailService(userId);

  // fetch system user roles
  const { _service } = useGetSystemUserRolesService();
  const systemUserRoles = _service?.data?.roles;

  // Stop upload photo and cancel current request
  const removeFile = () => cancelRequests('Request Cancelled!');

  // Submit Update Form
  const handleSubmit = useCallback(
    async values => {
      const { birthdate, telephone, photo, ...rest } = values;
      const valueToUse = {
        ...rest,
        birthdate: FormatUtils.convertPostDate(birthdate),
        telephone: FormatUtils.unFormatPhoneNumber(telephone),
        ...(photo ? { photo } : {})
      };
      setLoading(true, async () => {
        const serviceFn = () => SystemUserService.updateSystemUser(userId, valueToUse);
        try {
          await serviceFn();

          // update profile info if the user update is the profile itself
          if (isItMe) {
            // ...and reset them to redux store
            const newProfileData = await ProfileService.getProfileDetail();
            dispatch(updateUserProfile({ ...newProfileData?.data?.data }));
          }

          // re-fetch user detail
          handleRefresh();

          // remove photo component content(state)
          setSelectedFiles([]);

          setTimeout(() => {
            history.push('/user-management');
          }, 2000);
        } catch (err) {
          console.log('Error -> ', err);
        } finally {
          setLoading(false);
        }
      });
    },
    [setLoading, dispatch, userId, isItMe, history, handleRefresh]
  );

  // Handle update system user password
  const handlePasswordChange = useCallback(
    async values => {
      setPasswordLoading(true, async () => {
        try {
          await SystemUserService.updateSystemUserPwd(userId, values);
          passwordFormRef.current.handleReset();
        } catch (err) {
          console.log('Error -> ', err);
        } finally {
          setPasswordLoading(false);
        }
      });
    },
    [setPasswordLoading, userId]
  );

  // DELETE SYSTEM USER
  const handleDelete = async () => {
    setDeleteLoading(true, async () => {
      try {
        await SystemUserService.deleteSystemUser(userId);

        setTimeout(() => {
          history.replace(`/user-management`);
        }, 2000);
      } catch (err) {
        console.log('Error -> ', err);
      } finally {
        setDeleteLoading(false);
      }
    });
  };

  // initial open state for "delete confirm modal"
  const setModal = () => setConfirmAlert(true);

  // confirm modal confirm
  const handleDeleteConfirm = () => {
    setConfirmAlert(false);
    handleDelete();
  };

  // confirm modal cancel
  const handleDeleteCancel = () => setConfirmAlert(false);

  // variables - states
  const isDataLoading = !service.data && (!service.isInitialized || service.isLoading);
  const isDataExist = !!service.data && !service.isNetworkError && !service.isError;
  const { id, photo_url, ...rest } = !!service.data && service?.data;
  const initialVal = isEdit ? { ...rest } : EnvDefaults.updateProfileDefaults;
  const gridCondition = isItMe ? 12 : 8;

  return (
    <>
      <FusePageCarded
        header={<Breadcrumb title="Kullanıcı Yönetimi" />}
        contentToolbar={
          <Typography
            variant="subtitle1"
            className="m-0 p-24 tracking-wider font-semibold text-gray-800">
            Kullanıcı Bilgilerini Düzenle
          </Typography>
        }
        content={
          <div className="p-24">
            <Loader loading={isDataLoading} />
            {isDataExist ? (
              <>
                {/* System User Detail Info Card */}
                <SystemUserCard handleRefresh={handleRefresh} user={service?.data} />

                <Grid container spacing={2}>
                  {/* System User Info Update */}
                  <Grid item xs={12} sm={gridCondition}>
                    <CustomCard title="Kullanıcı Bilgileri" contentClassName="px-12">
                      <Formik
                        enableReinitialize
                        initialValues={initialVal}
                        validationSchema={ValidationSchema.systemUserCrudSchema(isEdit)}
                        onSubmit={handleSubmit}>
                        <Form>
                          <SystemUserForm
                            roles={systemUserRoles}
                            isEdit={true}
                            loading={loading}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            removeFile={removeFile}
                            handleDelete={setModal}
                            deleteLoading={deleteLoading}
                            isItMe={isItMe}
                          />
                        </Form>
                      </Formik>
                    </CustomCard>
                  </Grid>

                  {/* Change Password */}
                  {!isItMe && (
                    <Grid item xs={12} sm={4}>
                      <CustomCard title="Parola Değiştir" icon="bxs-lock-open">
                        <Formik
                          innerRef={passwordFormRef}
                          enableReinitialize
                          initialValues={EnvDefaults.changePasswordFormDefaults}
                          validationSchema={ValidationSchema.changePasswordSchema}
                          onSubmit={handlePasswordChange}>
                          <Form>
                            <ChangePasswordForm loading={passwordLoading} />
                          </Form>
                        </Formik>
                      </CustomCard>
                    </Grid>
                  )}
                </Grid>
              </>
            ) : (
              !isDataLoading && <NoDataTableComponent />
            )}

            {/* Delete user - confirmation modal dialog */}
            <ConfirmDialog
              open={confirmAlert}
              cancelText="Vazgeç"
              title="Kullanıcı Sil"
              confirmText="Sil"
              onConfirm={handleDeleteConfirm}
              onCancel={handleDeleteCancel}
            />
          </div>
        }
      />
    </>
  );
};

export default SystemUserEdit;
